import rows from "../extended/may2025Classification.json";
import React, { useContext } from "react";
import { MaturaReviewContext } from "../../components/MaturaExamRunner/MaturaReviewContext";
import { getExtendedOriginalById } from "../original/extendedOriginalTaskRegistry";
import { getVariantCount as exponentialCount } from "../../generators/dzial3_funkcje/temat6_zadania_uzupelniajace_maturalne/podtemat4_rozszerzenie_types/MaturaExponentialModelOpenTask";
import { getVariantCount as diceCount } from "../../generators/dzial9_prawdopodobienstwo/temat4_zadania_uzupelniajace_maturalne/podtemat4_rozszerzenie_types/MaturaConditionalDiceFacesOpenTask";
import { getVariantCount as absoluteCount } from "../../generators/dzial2_algebra/temat5_zadania_uzupelniajace_maturalne/podtemat4_rozszerzenie_types/MaturaAbsoluteValueInequalityOpenTask";
import { getMayVariantCount } from "../../generators/dzial5_trygonometria/temat4_zadania_uzupelniajace_maturalne/podtemat4_rozszerzenie_types/MaturaTrigEquationFactorOpenTask";
import { getVariantCounts as vietaCounts } from "../../generators/dzial3_funkcje/temat6_zadania_uzupelniajace_maturalne/podtemat4_rozszerzenie_types/MaturaQuadraticVietaParamOpenTask";
import { getVariantCounts as numericalCounts } from "../extended/May2025NumericalTasks";
import { getVariantCounts as proofCounts } from "../extended/May2025ProofTasks";
import { CalculationAssessment, checkCalculationAssessment, getOriginalPdfUrl } from "../extended/May2025Assessment";
import MaturaPdfPreview from "../../components/MaturaPdfPreview";
import MaturaOriginalSourceLinks from "../../components/MaturaOriginalSourceLinks";

export const sheet = { id: "cke-r-2025-maj", title: "Matura rozszerzona z matematyki — maj 2025",
  subtitle: "Formuła 2023 · 12 grup zadań · 13 punktowanych części", sourceLabel: "CKE • Poziom rozszerzony",
  taskCount: 13, maxPoints: 50, sessionType: "main" };
const moduleFor = (number) => getExtendedOriginalById(`cke-r-2025-maj-${number.replace(".", "-")}`).module;
export function generateTaskVariant(taskNumber, variant = "random", options = {}) {
  const row = rows.find((r) => r.taskNo === String(taskNumber));
  if (!row) throw new Error("Unknown extended May task number");
  if (row.taskNo.startsWith("12.") && !options.single) {
    const seed = options.seed ?? `${Date.now()}-${Math.random()}`;
    const linked = ["12.1", "12.2"].map((number) => generateTaskVariant(number, variant, { ...options, seed, single: true }));
    return { ...linked.find((r) => r.meta.taskNo === row.taskNo), linkedTasks: linked };
  }
  const task = moduleFor(row.taskNo).generateTask({ ...options, variant, session: "2025_MAJ" });
  return { index: rows.indexOf(row), task: { ...task, points: row.maxPoints, assessmentMode: "self", displayBaseNumber: Number(row.taskNo.split(".")[0]) },
    meta: { ...row, assessmentMode: "self", quizKind: "open" } };
}
export function generatePagedTasks(variant = "original", options = {}) {
  // Both cone parts must share their data even when the complete paper is randomized.
  const seed = options.seed ?? `${Date.now()}-${Math.random()}`;
  return rows.map((row) => generateTaskVariant(row.taskNo, variant, {
    ...options, single: true, seed: `${seed}:${row.taskNo.startsWith("12.") ? "cone" : row.taskNo}`,
  }));
}
export function generateTasks(variant, options) { return generatePagedTasks(variant, options).map((r) => r.task); }
export function getVariantCounts() { return { "1": exponentialCount(), "4": diceCount(), "5": absoluteCount(),
  "9": getMayVariantCount(), "11": vietaCounts()["same-sign-distance"], ...numericalCounts(), ...proofCounts() }; }
const adapter = (task) => moduleFor(task.source.taskNumber);
function OriginalPdfLink({ task }) {
  const review = useContext(MaturaReviewContext);
  if (review.interactive && !review.reviewAllowed) return null;
  return <MaturaPdfPreview url={getOriginalPdfUrl(task)} title={`Arkusz CKE · maj 2025 · rozszerzenie · zadanie ${task.source.taskNumber}`}
    label="Zobacz zadanie w oryginalnym PDF · maj 2025" />;
}
export function renderTask(task) {
  return <>{adapter(task).renderTask(task)}<MaturaOriginalSourceLinks task={task} />
    <OriginalPdfLink task={task} /></>;
}
export function renderInput(task, answer = {}, onChange, disabled) {
  const module = adapter(task);
  if (["2", "7", "12.1", "3", "6", "8", "10", "12.2"].includes(String(task.source.taskNumber)))
    return module.renderInput(task, answer, onChange, disabled);
  return <CalculationAssessment task={task} answer={answer} onChange={onChange} disabled={disabled}
    renderTaskPreview={() => module.renderTask(task)}
    fields={module.renderInput(task, answer, (next) => onChange({ ...next, reviewConfirmed: false }), disabled)}
    explanation={module.renderExplanation(task)} result={module.checkAnswer(task, answer)} />;
}
export function checkAnswer(task, answer) { return checkCalculationAssessment(task, answer); }
export function renderAnswer(task) { return adapter(task).renderAnswer(task); }
export function renderExplanation(task) { return adapter(task).renderExplanation(task); }
export function renderUserAnswer(answer, task) { return adapter(task).renderUserAnswer?.(answer, task); }
export function getInteractionSupport() { return { renderInput: true, checkAnswer: true, renderUserAnswer: true }; }
